import { useState, useCallback, useContext } from 'react';
import { CSVLink } from 'react-csv';
import { BsDownload } from 'react-icons/bs';
import dayjs from 'dayjs';

import Button from '../button/button.component';
import Filter from '../filter/filter.component';
import SearchBar from '../search-bar/search-bar.component';

import { ToastContext } from '../../../contexts/toast.context';

import {
  Option,
  Select,
  Subtitle
} from '../../../styles/page.styles';

import { RowContainer } from '../../../styles/component.styles';

/**
 * Reusable table controls component with search, filter, page size, and CSV export.
 *
 * @param {Object} props
 * @param {string} props.search - Current search value
 * @param {Function} props.setSearch - Set search value
 * @param {Function} props.onSearchSubmit - Called when search is submitted
 * @param {number} props.size - Current page size
 * @param {Function} props.setSize - Set page size
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.setFilters - Set filter values
 * @param {Array} props.filterConfig - Filter configuration array
 * @param {string} props.entityName - Name of the entity for display (e.g., "orders", "products")
 * @param {Function} props.exportFn - Async function that fetches export data
 * @param {Function} props.formatExportData - Function that formats raw data into CSV rows
 * @param {Array} props.exportHeaders - Array of header strings for CSV
 * @param {React.ReactNode} props.additionalButtons - Additional buttons to render
 * @param {boolean} props.showSearch - Whether to show search bar (default: true)
 * @param {boolean} props.showFilters - Whether to show filters (default: true if filterConfig provided)
 * @param {boolean} props.showExport - Whether to show export button (default: true if exportFn provided)
 * @param {boolean} props.showPageSize - Whether to show page size selector (default: true)
 */
const TableControls = ({
  search = '',
  setSearch,
  onSearchSubmit,
  size = 25,
  setSize,
  filters = {},
  setFilters,
  filterConfig = [],
  entityName = 'items',
  exportFn,
  formatExportData,
  exportHeaders = [],
  additionalButtons,
  showSearch = true,
  showFilters,
  showExport,
  showPageSize = true
}) => {
  const [csvData, setCsvData] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  const { errorToast, successToast } = useContext(ToastContext);

  // Determine visibility defaults
  const shouldShowFilters = showFilters !== undefined ? showFilters : filterConfig.length > 0;
  const shouldShowExport = showExport !== undefined ? showExport : !!exportFn;

  const handleFilterChange = useCallback((newFilters) => {
    if (setFilters) {
      setFilters(newFilters);
    }
  }, [setFilters]);

  const prepareExport = async () => {
    if (!exportFn || !formatExportData) {
      errorToast('Export not configured');
      return;
    }

    setExportLoading(true);
    try {
      const rawData = await exportFn(filters);
      const formattedRows = formatExportData(rawData);

      const data = [exportHeaders, ...formattedRows];

      setCsvData(data);
      successToast(`Export ready: ${formattedRows.length} ${entityName}`);
    } catch (err) {
      console.error('Export Error:', err);
      errorToast(`Failed to prepare ${entityName} export`);
    }
    setExportLoading(false);
  };

  return (
    <>
      {/* Action buttons row */}
      {(additionalButtons || shouldShowExport) && (
        <RowContainer margin={'0 0 20px 0'} style={{ gap: '10px', flexWrap: 'wrap' }}>
          {additionalButtons}
          {shouldShowExport && (
            <>
              <Button onClick={prepareExport} disabled={exportLoading}>
                {exportLoading ? 'Loading...' : 'Prepare Export'}
              </Button>
              {csvData.length > 1 && (
                <CSVLink
                  data={csvData}
                  filename={`${entityName}-export-${dayjs().format('YYYY-MM-DD')}.csv`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '8px 16px',
                    background: '#28a745',
                    color: '#fff',
                    borderRadius: '4px',
                    textDecoration: 'none'
                  }}
                >
                  <BsDownload /> Download CSV ({csvData.length - 1} {entityName})
                </CSVLink>
              )}
            </>
          )}
        </RowContainer>
      )}

      {/* Search bar */}
      {showSearch && setSearch && (
        <SearchBar
          search={search}
          setSearch={setSearch}
          submitSearch={onSearchSubmit}
        />
      )}

      {/* Filters */}
      {shouldShowFilters && filterConfig.length > 0 && (
        <>
          <Subtitle>Filter</Subtitle>
          <Filter
            filters={filterConfig}
            defaultValues={filters}
            onFilterChange={handleFilterChange}
          />
        </>
      )}

      {/* Page size selector */}
      {showPageSize && setSize && (
        <Select
          value={size}
          onChange={(e) => setSize(parseInt(e.target.value, 10))}
          maxWidth={'100px'}
          marginBottom={'20px'}
        >
          <Option value={10}>10</Option>
          <Option value={25}>25</Option>
          <Option value={50}>50</Option>
          <Option value={100}>100</Option>
        </Select>
      )}
    </>
  );
};

export default TableControls;
